import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext, closestCenter, pointerWithin, DragOverlay, MeasuringStrategy,
  useSensor, useSensors, MouseSensor, TouchSensor,
} from '@dnd-kit/core';
import type { CollisionDetection, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Icon, Plus, MapPin, Clock, Sparkles, ArrowDown } from '../../ui/Icon';
import type { TransferKind } from '../../../api/directions.api';
import { formatLegDuration } from '../../../utils/legFormatting';
import { DayDropBar } from './DayDropBar';
import { dayIdFromDrop, DAY_DROP_PREFIX } from './dayDropTarget';
import { useArmedDayDrop } from './useArmedDayDrop';
import { ConfirmDayMove } from './ConfirmDayMove';
import Spinner from '../../ui/Spinner';
import { ActivityCard } from './ActivityCard';
import { MaskedStopCard } from './MaskedStopCard';
import { StopPhotoStrip } from './StopPhotoStrip';
import { suggestedStartTime } from '../../../utils/activityOrder';
import type { Activity, ActivityType, ItineraryDay, TripPhoto } from '../../../types';

interface Props {
  day: ItineraryDay;
  /** Every day of the trip — the drop targets shown while a card is dragged. */
  days?: ItineraryDay[];
  /** Given the cards being dragged and the day they were dropped on. */
  onMoveToDay?: (activityIds: string[], targetDayId: string) => void;
  /** The drop bar and the bulk action bar share a spot; only one may be up. */
  onDraggingChange?: (dragging: boolean) => void;
  /** Seconds of travel from a stop to the next one, when they are known. */
  secondsAfter?: (activityId: string) => number | undefined;
  /** ADR-0031: a flight shows nothing here (its card says it); a transfer says so. */
  transferAfter?: (activityId: string) => TransferKind | undefined;
  /** Accepts the hour the app worked out for a stop. Absent = no suggestions. */
  onAcceptSuggestedTime?: (activity: Activity, startTime: string) => void;
  /** Ask the assistant to plan this day. Absent = the button is not offered. */
  onPlanWithAi?: () => void;
  /** Jump to the saved places, to schedule some here. */
  onBrowseSaved?: () => void;
  prevDayLastActivity?: Activity | null;
  onAddActivity: (v: { dayId: string }) => void;
  onEditActivity: (a: Activity) => void;
  onDeleteActivity: (a: Activity) => void;
  onDragEnd: (dayId: string, orderedIds: string[]) => void;
  onToggleStartFromPrev?: (enabled: boolean) => void;
  startFromPrevPending?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  lastActivityLocked?: boolean;
  getMapsUrl?: (a: Activity) => string | null;
  /**
   * The saved place behind a stop, when there is one. "Visited" lives on the
   * venue (`Venue.visited`), so a stop without one simply gets no toggle.
   */
  visitedOf?: (a: Activity) => boolean | undefined;
  onToggleVisited?: (a: Activity) => void;
  /** ADR-0042: the photos taken at each stop, under its card. */
  photosOf?: (activityId: string) => TripPhoto[];
  onOpenPhoto?: (photos: TripPhoto[], index: number) => void;
}

// A stray isOrigin card can only render when it agrees with the day's own flag — otherwise
// a leftover/out-of-sync row would show as a locked, undeletable card with no way for the
// user to get rid of it. The flag stays authoritative for what's actually "active".
function visibleActivities(day: ItineraryDay): Activity[] {
  const list = day.startFromPrevStop ? day.activities : day.activities.filter(a => !a.isOrigin);
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function TimelineRail({ day, days, prevDayLastActivity, onAddActivity, onEditActivity, onDeleteActivity, onDragEnd, onMoveToDay, onDraggingChange, secondsAfter, transferAfter, onAcceptSuggestedTime, onPlanWithAi, onBrowseSaved, onToggleStartFromPrev, startFromPrevPending, selectedIds, onToggleSelect, lastActivityLocked, getMapsUrl, visitedOf, onToggleVisited, photosOf, onOpenPhoto }: Props) {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<Activity[]>(() => visibleActivities(day));

  useEffect(() => {
    setActivities(visibleActivities(day));
  }, [day]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const activeActivity = activeId ? activities.find(a => a.id === activeId) : null;
  const dayDrop = useArmedDayDrop();
  const [pendingMove, setPendingMove] = useState<{ ids: string[]; dayId: string } | null>(null);

  // Mouse: 8px of movement before drag activates — allows click-to-edit without accidentally dragging.
  // Touch: press-and-hold (200ms) instead of a distance threshold — on mobile a distance-based pointer
  // drag gets swallowed by the browser's scroll gesture, so the finger could barely reorder. The delay
  // lets a quick swipe scroll the list while a deliberate hold starts the drag (tolerance allows small
  // finger jitter during the hold without cancelling).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  // Two kinds of target share one context and they need different rules. A day
  // chip only wins when the pointer is actually inside it — with `closestCenter`
  // alone, dragging a card towards the bottom of the list would put the bar's
  // centre nearest and move the stop to another day nobody aimed at. The stops
  // themselves keep `closestCenter`, which is what makes reordering feel right.
  const collisionDetection: CollisionDetection = args => {
    const onDay = pointerWithin(args).filter(c => String(c.id).startsWith(DAY_DROP_PREFIX));
    return onDay.length > 0 ? onDay : closestCenter(args);
  };

  // ID of the last non-origin activity when it must stay last (next day starts from it)
  const lockedId = lastActivityLocked
    ? [...activities].filter(a => !a.isOrigin).at(-1)?.id ?? null
    : null;

  // Index of the first non-origin activity (items can't be dragged before isOrigin)
  const firstNonOriginIdx = activities.findIndex(a => !a.isOrigin);

  /** The cards a drag carries: the whole selection when the grabbed card is in it. */
  function draggedIds(activeIdStr: string): string[] {
    return (selectedIds && selectedIds.size > 1 && selectedIds.has(activeIdStr))
      ? activities.filter(a => selectedIds.has(a.id) && !a.isOrigin).map(a => a.id)
      : [activeIdStr];
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const armedDayId = dayDrop.armedDayId;
    setActiveId(null);
    onDraggingChange?.(false);
    dayDrop.reset();
    if (!over || active.id === over.id) return;

    const activeIdStr = active.id as string;
    const overIdStr   = over.id  as string;

    // Dropped on another day rather than on a card in this one. Checked first:
    // the day targets are not in `activities`, so every lookup below would miss.
    // A chip the pointer only passed over is not a choice: the drop is ignored.
    const targetDayId = dayIdFromDrop(overIdStr);
    if (targetDayId) {
      if (targetDayId === armedDayId) setPendingMove({ ids: draggedIds(activeIdStr), dayId: targetDayId });
      return;
    }

    const oldIdx = activities.findIndex(a => a.id === activeIdStr);
    let   newIdx = activities.findIndex(a => a.id === overIdStr);
    if (oldIdx < 0 || newIdx < 0) return;

    // Clamp so nothing lands before the first non-origin slot
    if (firstNonOriginIdx > 0) newIdx = Math.max(newIdx, firstNonOriginIdx);

    if (newIdx === oldIdx) return;

    // Pack drag: dragging a selected item when multiple are selected
    if (selectedIds && selectedIds.size > 1 && selectedIds.has(activeIdStr)) {
      const selectedInOrder = activities
        .filter(a => selectedIds.has(a.id))
        .sort((a, b) => activities.indexOf(a) - activities.indexOf(b));
      const rest = activities.filter(a => !selectedIds.has(a.id));
      const overInRest = rest.findIndex(a => a.id === overIdStr);

      // If the drop target is also selected, fall back to normal arrayMove
      if (overInRest === -1) {
        const reordered = arrayMove(activities, oldIdx, newIdx);
        setActivities(reordered);
        onDragEnd(day.id, reordered.map(a => a.id));
        return;
      }

      // Insert the selected group after target when dragging down, before when dragging up
      const insertIdx = overInRest + (newIdx > oldIdx ? 1 : 0);
      const result = [
        ...rest.slice(0, insertIdx),
        ...selectedInOrder,
        ...rest.slice(insertIdx),
      ];
      setActivities(result);
      onDragEnd(day.id, result.map(a => a.id));
      return;
    }

    // Normal single drag
    const reordered = arrayMove(activities, oldIdx, newIdx);
    setActivities(reordered);
    onDragEnd(day.id, reordered.map(a => a.id));
  }

  // Active only if the flag is set AND the isOrigin activity actually exists.
  // They can diverge when an isOrigin is deleted (e.g. via AI) without resetting the flag.
  const isStartFromPrevActive = day.startFromPrevStop && day.activities.some(a => a.isOrigin);

  const packSize = activeId && selectedIds && selectedIds.size > 1 && selectedIds.has(activeId)
    ? selectedIds.size
    : 0;

  return (
    <div className="relative">
      {/* Vertical rail line */}
      <div className="absolute left-[18px] top-0 bottom-0 w-px bg-slate-100 dark:bg-slate-800 rounded-full" />

      {/* Once active, the origin card below is self-explanatory (teal + pin + "Start here"
          badge + its own disable button) — showing this banner too would just repeat the
          same venue name a second time. Only show it as an invitation to turn the feature on. */}
      {prevDayLastActivity && !isStartFromPrevActive && (
        <div className="pl-10 mb-3">
          <button
            type="button"
            disabled={startFromPrevPending}
            onClick={() => onToggleStartFromPrev?.(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 text-xs text-slate-400 dark:text-slate-500 hover:border-slate-300 dark:hover:border-slate-600 transition-all cursor-pointer text-left disabled:opacity-60 disabled:cursor-wait"
          >
            {startFromPrevPending
              ? <Spinner size="xs" branded />
              : <Icon as={MapPin} size={12} className="shrink-0 text-teal-400" />}
            <span className="flex-1 min-w-0 truncate">
              {t('itinerary.continuesFrom')}: <span className="font-medium text-slate-600 dark:text-slate-300">{prevDayLastActivity.venueName ?? prevDayLastActivity.title}</span>
            </span>
            <span className="shrink-0 text-2xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400">
              {t('itinerary.startFromPrev.inactive')}
            </span>
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        // The day targets mount when the drag has already begun, so the default
        // measure-once-at-drag-start would never give them a rect to hit.
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={e => { setActiveId(e.active.id as string); onDraggingChange?.(true); }}
        onDragOver={dayDrop.onDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => { setActiveId(null); onDraggingChange?.(false); dayDrop.reset(); }}
      >
        {activeId && days && onMoveToDay && (
          <DayDropBar
            days={days}
            currentDayId={day.id}
            hoverDayId={dayDrop.hoverDayId}
            armedDayId={dayDrop.armedDayId}
          />
        )}

        <SortableContext items={activities.map(a => a.id)} strategy={verticalListSortingStrategy}>
          <div className="pl-10 space-y-2">
            {activities.length === 0 ? (
              /* The emptiest day is the moment of highest intent in the whole
                 product, and it used to be the only place with no button. */
              <div className="py-3">
                <div className="absolute left-[12px] w-3 h-3 rounded-full border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" />
                <p className="text-sm text-slate-400 mb-2.5">{t('itinerary.empty.noActivities')}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onAddActivity({ dayId: day.id })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-50 dark:bg-teal-900/25 border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors cursor-pointer text-xs font-semibold"
                  >
                    <Icon as={Plus} size={13} />
                    {t('itinerary.empty.addStop')}
                  </button>
                  {onPlanWithAi && (
                    <button
                      type="button"
                      onClick={onPlanWithAi}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-violet-200 dark:border-violet-800 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors cursor-pointer text-xs font-semibold"
                    >
                      <Icon as={Sparkles} size={13} />
                      {t('itinerary.empty.planWithAi')}
                    </button>
                  )}
                  {onBrowseSaved && (
                    <button
                      type="button"
                      onClick={onBrowseSaved}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer text-xs font-semibold"
                    >
                      <Icon as={MapPin} size={13} />
                      {t('itinerary.empty.fromSaved')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              activities.map((activity, idx) => {
                const legSecs = idx < activities.length - 1 ? secondsAfter?.(activity.id) : undefined;
                const transfer = idx < activities.length - 1 ? transferAfter?.(activity.id) : undefined;
                // The hour this stop would start at, offered only where one is
                // missing. The leg leaving the previous card is the hop into
                // this one, which is why it is keyed by that stop and not this.
                const previous = activities[idx - 1];
                const hopSecs = previous ? secondsAfter?.(previous.id) : undefined;
                const suggestion = !activity.startTime && !activity.isOrigin && onAcceptSuggestedTime
                  ? suggestedStartTime(
                      previous,
                      hopSecs === undefined ? undefined : Math.round(hopSecs / 60),
                      day.startTime,
                    )
                  : null;
                return (
                <div key={activity.id} className="relative">
                  {/* Rail dot */}
                  <div
                    className="absolute -left-[28px] top-3 w-2 h-2 rounded-full border-2 border-white dark:border-slate-950 z-10"
                    style={{ background: CAT_DOT[activity.type] ?? '#94a3b8' }}
                  />
                  {activity.maskedForAgency ? <MaskedStopCard activity={activity} /> : <ActivityCard
                    activity={activity}
                    isFirst={idx === 0}
                    onEdit={onEditActivity}
                    onDelete={onDeleteActivity}
                    isSelected={selectedIds?.has(activity.id)}
                    onToggleSelect={(!activity.isOrigin && onToggleSelect) ? () => onToggleSelect(activity.id) : undefined}
                    isLocked={activity.id === lockedId}
                    onToggleStartFromPrev={activity.isOrigin ? onToggleStartFromPrev : undefined}
                    startFromPrevPending={activity.isOrigin ? startFromPrevPending : undefined}
                    departureTime={activity.isOrigin ? day.startTime : undefined}
                    suggestion={suggestion}
                    onAcceptSuggestion={onAcceptSuggestedTime && suggestion
                      ? () => onAcceptSuggestedTime(activity, suggestion.time)
                      : undefined}
                    getMapsUrl={getMapsUrl}
                    visited={visitedOf?.(activity)}
                    onToggleVisited={
                      onToggleVisited && visitedOf?.(activity) !== undefined
                        ? () => onToggleVisited(activity)
                        : undefined
                    }
                  />}
                  {photosOf && onOpenPhoto && !activity.isOrigin && (
                    <StopPhotoStrip photos={photosOf(activity.id)} onOpen={i => onOpenPhoto(photosOf(activity.id), i)} />
                  )}
                  {transfer === 'TRANSFER' && (
                    <div className="flex items-center gap-1.5 pl-3 pt-2 text-slate-400">
                      <Icon as={ArrowDown} size={11} className="shrink-0" />
                      <span className="text-2xs">{t('map.travelMode.TRANSFER')}</span>
                    </div>
                  )}
                  {legSecs !== undefined && (
                    <div className="flex items-center gap-1.5 pl-3 pt-2 text-slate-400">
                      <Icon as={Clock} size={11} className="shrink-0" />
                      <span className="text-2xs tabular-nums">{formatLegDuration(legSecs)}</span>
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeActivity && (
            <div className="relative pointer-events-none">
              {packSize > 1 && (
                <>
                  <div className="absolute inset-0 translate-x-2.5 translate-y-2.5 bg-white dark:bg-slate-900 rounded-xl border border-teal-200 dark:border-teal-800 shadow-md opacity-50" />
                  <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 bg-white dark:bg-slate-900 rounded-xl border border-teal-200 dark:border-teal-800 shadow-md opacity-75" />
                </>
              )}
              <div className="relative bg-white dark:bg-slate-900 rounded-xl border border-teal-300 dark:border-teal-700 shadow-lg p-3 opacity-95">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{activeActivity.title}</p>
                {packSize > 1 && (
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-0.5 font-semibold">+{packSize - 1} more</p>
                )}
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <ConfirmDayMove
        moving={pendingMove ? activities.filter(a => pendingMove.ids.includes(a.id)) : []}
        target={pendingMove ? days?.find(d => d.id === pendingMove.dayId) ?? null : null}
        onConfirm={() => {
          if (pendingMove) onMoveToDay?.(pendingMove.ids, pendingMove.dayId);
          setPendingMove(null);
        }}
        onCancel={() => setPendingMove(null)}
      />

      {/* Add activity */}
      <button
        onClick={() => onAddActivity({ dayId: day.id })}
        className="mt-3 ml-10 flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-50 dark:bg-teal-900/25 border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/40 hover:border-teal-400 dark:hover:border-teal-500 transition-all cursor-pointer text-sm font-semibold"
      >
        <Icon as={Plus} size={14} />
        {t('itinerary.modal.addActivity')}
      </button>
    </div>
  );
}

const CAT_DOT: Record<ActivityType, string> = {
  TRANSPORT:    '#0ea5e9',
  PARKING:      '#0ea5e9',
  BEACH:        '#c87a1a',
  NATURE:       '#c87a1a',
  ACCOMMODATION:'#6366f1',
  MONUMENT:     '#8b5cf6',
  VIEWPOINT:    '#8b5cf6',
  NEIGHBORHOOD: '#8b5cf6',
  RESTAURANT:   '#f97316',
  CAFE:         '#f97316',
  EXPERIENCE:   '#c87a1a',
  PARK:         '#c87a1a',
  SHOP:         '#c87a1a',
  OTHER:        '#94a3b8',
};
